import React, { useMemo, useState } from 'react';
import { FileDown, Download, ArrowUpRight, ArrowDownRight, Award, AlertTriangle, User, Ruler, Calculator, Plus, Clock, Wallet, PenTool, BookOpen, MessageSquare, Search, Globe, Book } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { MODULES } from '../../../content';
import { getModuleFromQuestionId, getModuleTrendDelta } from '../../../utils/activityHelpers';
import { StudentData } from '../pages/StudentDetailPanel';
const MODULE_ICONS: Record<string, LucideIcon> = {
  Ruler,
  Calculator,
  Plus,
  Clock,
  Wallet,
  PenTool,
  BookOpen,
  MessageSquare,
  Search,
  Globe
};
interface StudentReportCardProps {
  students: (StudentData & {
    avgMastery: number;
    trendDelta: number;
    daysPracticed: number;
    weakestModule: any;
    strongestModule: any;
  })[];
  selectedGroup: number;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  periodDays: number;
}
export function StudentReportCard({
  students,
  selectedGroup,
  getModuleMastery,
  periodDays
}: StudentReportCardProps) {
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const selectedStudent = useMemo(() => {
    return students.find(s => s.id === selectedStudentId) || null;
  }, [students, selectedStudentId]);
  const moduleStats = useMemo(() => {
    if (!selectedStudent) return [];
    const fm = selectedStudent.data.familyMastery || {};
    return MODULES.map(module => {
      const mastery = getModuleMastery(selectedStudent.id, module.id);
      const trend = getModuleTrendDelta(fm, selectedGroup, module.id);
      let questionsPracticed = 0;
      for (const [key, state] of Object.entries(fm)) {
        if (!key.includes(`_g${selectedGroup}`)) continue;
        const baseId = key.replace(/_g\d+$/, '');
        const familyModuleId = getModuleFromQuestionId(baseId);
        if (familyModuleId === module.id) {
          questionsPracticed += (state as any).attempts || 0;
        }
      }
      return {
        module,
        mastery,
        trend,
        questionsPracticed
      };
    });
  }, [selectedStudent, selectedGroup, getModuleMastery]);
  const handleExportCsv = () => {
    if (!selectedStudent) return;
    const headers = ['Vak', 'Beheersing (%)', 'Trend (%)', 'Vragen Geoefend'];
    const rows = moduleStats.map(m => {
      return [`"${m.module.title}"`, m.mastery, m.trend, m.questionsPracticed].join(',');
    });
    const csvContent = [`Leerling: ${selectedStudent.name}`, `Periode: Laatste ${periodDays} dagen`, '', headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `rapport_${selectedStudent.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const studentOptions = students.map(s => ({
    value: s.id,
    label: s.name
  }));
  return <Card className="p-5 border-slate-200 shadow-sm bg-white flex flex-col" data-id="element-4796">
      <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center mb-3 border border-indigo-100" data-id="element-4797">
        <FileDown className="w-6 h-6" data-id="element-4798" />
      </div>
      <h2 className="text-xl font-black text-slate-900 mb-2" data-id="element-4799">
        Per-leerling rapport
      </h2>
      <p className="text-slate-500 mb-5" data-id="element-4800">
        Bekijk en exporteer een gedetailleerd rapport per leerling. Ideaal om
        mee te geven aan ouders tijdens 10-minutengesprekken.
      </p>

      <div className="mb-2" data-id="element-4801">
        <Select options={studentOptions} value={selectedStudentId} onChange={e => setSelectedStudentId(e.target.value)} placeholder="Kies een leerling..." icon={<User className="w-4 h-4" data-id="element-4803" />} data-id="element-4802" />
      </div>

      {selectedStudent ? <div className="mt-4 space-y-6" data-id="element-4804">
          {/* Summary Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-4805">
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100" data-id="element-4806">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-4807">
                Gemiddeld
              </div>
              <div className="text-xl font-black text-slate-900" data-id="element-4808">
                {selectedStudent.avgMastery}%
              </div>
            </div>
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100" data-id="element-4809">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-4810">
                Trend
              </div>
              <div className="text-xl font-black text-slate-900 flex items-center gap-1" data-id="element-4811">
                {selectedStudent.trendDelta > 0 ? <ArrowUpRight className="w-5 h-5 text-emerald-500" data-id="element-4812" /> : selectedStudent.trendDelta < 0 ? <ArrowDownRight className="w-5 h-5 text-rose-500" data-id="element-4813" /> : null}
                <span className={selectedStudent.trendDelta > 0 ? 'text-emerald-600' : selectedStudent.trendDelta < 0 ? 'text-rose-600' : 'text-slate-600'} data-id="element-4814">
                  {selectedStudent.trendDelta > 0 ? '+' : ''}
                  {selectedStudent.trendDelta}%
                </span>
              </div>
            </div>
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100" data-id="element-4815">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-4816">
                Dagen geoefend
              </div>
              <div className="text-xl font-black text-slate-900" data-id="element-4817">
                {selectedStudent.daysPracticed}
              </div>
            </div>
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100" data-id="element-4818">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-4819">
                Vragen totaal
              </div>
              <div className="text-xl font-black text-slate-900" data-id="element-4820">
                {moduleStats.reduce((sum, m) => sum + m.questionsPracticed, 0)}
              </div>
            </div>
          </div>

          {/* Highlights */}
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-4821">
            {selectedStudent.strongestModule && <div className="flex-1 flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-100" data-id="element-4822">
                <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center" data-id="element-4823">
                  <Award className="w-4 h-4" data-id="element-4824" />
                </div>
                <div data-id="element-4825">
                  <div className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider" data-id="element-4826">
                    Sterkste vak
                  </div>
                  <div className="text-sm font-bold text-emerald-900" data-id="element-4827">
                    {selectedStudent.strongestModule.title}
                  </div>
                </div>
              </div>}
            {selectedStudent.weakestModule && <div className="flex-1 flex items-center gap-3 p-3 rounded-xl bg-rose-50 border border-rose-100" data-id="element-4828">
                <div className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center" data-id="element-4829">
                  <AlertTriangle className="w-4 h-4" data-id="element-4830" />
                </div>
                <div data-id="element-4831">
                  <div className="text-[10px] font-bold text-rose-600 uppercase tracking-wider" data-id="element-4832">
                    Aandachtspunt
                  </div>
                  <div className="text-sm font-bold text-rose-900" data-id="element-4833">
                    {selectedStudent.weakestModule.title}
                  </div>
                </div>
              </div>}
          </div>

          {/* Table */}
          <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-4834">
            <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 grid grid-cols-[1fr_80px_60px_60px] md:grid-cols-[1fr_100px_60px_80px] gap-3" data-id="element-4835">
              <div className="text-xs font-bold text-slate-500 uppercase" data-id="element-4836">
                Vak
              </div>
              <div className="text-xs font-bold text-slate-500 uppercase" data-id="element-4837">
                Beheersing
              </div>
              <div className="text-xs font-bold text-slate-500 uppercase text-right" data-id="element-4838">
                Trend
              </div>
              <div className="text-xs font-bold text-slate-500 uppercase text-right" data-id="element-4839">
                Vragen
              </div>
            </div>
            <div className="divide-y divide-slate-100" data-id="element-4840">
              {moduleStats.map(stat => {
            const IconComponent = MODULE_ICONS[stat.module.icon] || Book;
            const colorClass = stat.mastery >= 70 ? 'bg-emerald-500' : stat.mastery >= 40 ? 'bg-amber-500' : 'bg-rose-500';
            const textColorClass = stat.mastery >= 70 ? 'text-emerald-600' : stat.mastery >= 40 ? 'text-amber-600' : 'text-rose-600';
            return <div key={stat.module.id} className="px-4 py-3 grid grid-cols-[1fr_80px_60px_60px] md:grid-cols-[1fr_100px_60px_80px] gap-3 items-center hover:bg-slate-50 transition-colors" data-id="element-4841">
                    <div className="flex items-center gap-3 min-w-0" data-id="element-4842">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100 ${stat.module.color}`} data-id="element-4843">
                        <IconComponent className="w-4 h-4" data-id="element-4844" />
                      </div>
                      <div className="font-bold text-sm text-slate-900 truncate" data-id="element-4845">
                        {stat.module.title}
                      </div>
                    </div>

                    <div data-id="element-4846">
                      <div className="flex justify-between items-end mb-1" data-id="element-4847">
                        <span className={`text-xs font-bold ${textColorClass}`} data-id="element-4848">
                          {stat.mastery}%
                        </span>
                      </div>
                      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-4849">
                        <div className={`h-full rounded-full ${colorClass}`} style={{
                    width: `${stat.mastery}%`
                  }} data-id="element-4850" />
                      </div>
                    </div>

                    <div className="flex justify-end" data-id="element-4851">
                      {stat.trend > 0 ? <div className="flex items-center text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded text-xs font-bold" data-id="element-4852">
                          <ArrowUpRight className="w-3 h-3 mr-0.5" data-id="element-4853" />
                          {stat.trend}%
                        </div> : stat.trend < 0 ? <div className="flex items-center text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded text-xs font-bold" data-id="element-4854">
                          <ArrowDownRight className="w-3 h-3 mr-0.5" data-id="element-4855" />
                          {Math.abs(stat.trend)}%
                        </div> : <div className="text-slate-400 text-xs font-medium" data-id="element-4856">
                          —
                        </div>}
                    </div>

                    <div className="text-right text-sm font-medium text-slate-600" data-id="element-4857">
                      {stat.questionsPracticed}
                    </div>
                  </div>;
          })}
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3 pt-4 border-t border-slate-100" data-id="element-4858">
            <Button onClick={handleExportCsv} variant="outline" className="w-full justify-start text-left border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300" data-id="element-4859">
              <Download className="w-4 h-4 mr-3 text-indigo-500" data-id="element-4860" />
              <div data-id="element-4861">
                <div className="font-bold text-slate-900" data-id="element-4862">
                  Exporteer als CSV
                </div>
                <div className="text-xs text-slate-500 font-normal" data-id="element-4863">
                  Download dit rapport als spreadsheet
                </div>
              </div>
            </Button>

            <Button disabled variant="outline" className="w-full justify-start text-left opacity-70 border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300" data-id="element-4864">
              <FileDown className="w-4 h-4 mr-3 text-slate-400" data-id="element-4865" />
              <div data-id="element-4866">
                <div className="font-bold text-slate-900 flex items-center gap-2" data-id="element-4867">
                  Rapport in PDF
                  <span className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-4868">
                    Binnenkort
                  </span>
                </div>
                <div className="text-xs text-slate-500 font-normal" data-id="element-4869">
                  Gedetailleerde voortgang per leerling
                </div>
              </div>
            </Button>
          </div>
        </div> : <div className="mt-4 flex flex-col items-center justify-center p-8 text-center border-2 border-dashed border-slate-200 rounded-xl bg-slate-50 flex-1" data-id="element-4870">
          <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center mb-3 text-slate-400" data-id="element-4871">
            <User className="w-6 h-6" data-id="element-4872" />
          </div>
          <h3 className="text-sm font-bold text-slate-900 mb-1" data-id="element-4873">
            Geen leerling geselecteerd
          </h3>
          <p className="text-xs text-slate-500 max-w-[200px]" data-id="element-4874">
            Selecteer een leerling hierboven om het individuele rapport te
            bekijken.
          </p>
        </div>}
    </Card>;
}